import { useEffect, useRef, useState } from "react";
import { Loader2, AlertCircle } from "lucide-react";
import { getArcQuote } from "@/lib/api";
import FxChip from "@/components/FxChip";

const DEBOUNCE_MS = 250;

/**
 * Read-only ARC → EUR preview. Fires GET /api/arc/quote with the
 * user's current input, debounced 250 ms. NEVER submits.
 *
 * Returns the rendered card and lifts the current state to the parent
 * via `onStateChange` so the redemption form shell can decide whether
 * the (still-disabled) submit button should appear ready or blocked.
 */
export default function QuotePreview({ amount, onStateChange }) {
  const [state, setState] = useState({
    kind: "idle",  // idle | loading | active | unavailable | invalid | error
    quote: null,
    message: "",
  });
  const timerRef = useRef(null);
  const reqId = useRef(0);

  useEffect(() => {
    if (timerRef.current) clearTimeout(timerRef.current);

    const trimmed = (amount ?? "").trim();
    if (trimmed === "") {
      const next = { kind: "idle", quote: null, message: "" };
      setState(next);
      onStateChange?.(next);
      return;
    }

    timerRef.current = setTimeout(async () => {
      const myReq = ++reqId.current;
      setState((prev) => ({ ...prev, kind: "loading" }));
      try {
        const quote = await getArcQuote(trimmed);
        if (myReq !== reqId.current) return;
        let next;
        if (quote.status === "unavailable") {
          next = {
            kind: "unavailable", quote: null,
            message: "ecosystem rate temporarily unavailable",
          };
        } else {
          next = { kind: "active", quote, message: "" };
        }
        setState(next);
        onStateChange?.(next);
      } catch (e) {
        if (myReq !== reqId.current) return;
        const status = e?.response?.status;
        let next;
        if (status === 400) {
          next = { kind: "invalid", quote: null, message: "Invalid amount." };
        } else {
          next = { kind: "error", quote: null, message: "Quote service unreachable." };
        }
        setState(next);
        onStateChange?.(next);
      }
    }, DEBOUNCE_MS);

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [amount]);

  const { kind, quote, message } = state;

  return (
    <div
      data-testid="redeem-quote-preview"
      data-quote-state={kind}
      className="rounded-md border border-white/10 bg-black/40 p-4"
    >
      {kind === "idle" && (
        <p className="font-mono text-[11px] text-white/40">
          Enter an ARC amount to preview the EUR equivalent at the
          current ecosystem rate.
        </p>
      )}

      {kind === "loading" && (
        <p className="flex items-center gap-2 font-mono text-xs text-white/60">
          <Loader2 className="h-3.5 w-3.5 animate-spin" /> calculating preview…
        </p>
      )}

      {kind === "active" && (
        <div data-testid="redeem-quote-result" data-pricing-mode={quote.pricing_mode || "legacy"}>
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/50">
            Preview
          </div>
          <div className="mt-1 flex items-baseline gap-3">
            <span className="font-display text-2xl text-[#39FF14] glow-green" data-testid="redeem-quote-eur">
              {quote.eur_amount}
            </span>
            <span className="font-display text-sm text-[#00F0FF]">EUR</span>
            <span className="ml-auto font-mono text-[11px] text-white/40">
              @ {quote.eur_per_arc} EUR / ARC · {quote.pricing_mode === "usd_peg_v1" ? "usd peg" : "ecosystem rate"}
            </span>
          </div>

          {/* Step 4.1 — usd_peg_v1 lane: surface USD value + FX provenance
              additively. Hidden for legacy quotes so the existing
              compact UI is unchanged. */}
          {quote.pricing_mode === "usd_peg_v1" && (
            <div className="mt-3 border-t border-white/10 pt-3 space-y-2" data-testid="redeem-quote-usd-peg">
              <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
                <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-white/50">
                  ARC value:
                </span>
                <span className="font-display text-base text-white" data-testid="redeem-quote-usd-value">
                  ${quote.usd_value}
                </span>
                <span className="font-mono text-[10px] text-white/40">
                  · estimated Assos365 credit:
                </span>
                <span className="font-display text-base text-[#39FF14]" data-testid="redeem-quote-eur-credit">
                  €{quote.eur_amount}
                </span>
              </div>
              <div>
                <FxChip snap={quote.fx_snapshot} testid="redeem-quote-fx-chip" compact />
              </div>
              <p className="font-mono text-[10px] text-white/40 leading-relaxed" data-testid="redeem-quote-peg-disclosure">
                ARC is temporarily valued at 1 USDT. Assos365 credit is
                converted to EUR using the current USD/EUR snapshot.
              </p>
            </div>
          )}

          <p className="mt-2 font-mono text-[10px] text-white/40">
            Rate locks at submit — future rate changes will not affect a
            submitted redemption.
          </p>
        </div>
      )}

      {(kind === "unavailable" || kind === "error" || kind === "invalid") && (
        <div className="flex items-start gap-2 text-[#FF3B30]" data-testid={`redeem-quote-${kind}`}>
          <AlertCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
          <p className="font-mono text-xs">{message}</p>
        </div>
      )}
    </div>
  );
}
